import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft, FiCheck, FiEye, FiFileText, FiPrinter, FiTruck, FiXCircle } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import { getItemsCached, getLocationsCached, getEmployeesCached, getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { formatAmount } from '../../utils/documentNumbering';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import ItemSelect from '../../components/ItemSelect';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus, enrichTransactionLines
} from '../../utils/transactionHelper';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import { generateStockFlow, deleteStockFlow, getAvailableBatches, getAvailableSerials, getItemStockCached, invalidateItemStock, listStockFlowRows, applyEntriesToStockCache } from '../../utils/stockFlowHelper';
import { costRowsChange } from '../../utils/itemCostEngine';
import { doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';

const TRAN_TYPE_CODE = 'ST'; // Uses same transaction type as Stock Transfer

// INV-15: save the document and the rows `addRows` adds in ONE commit
// (transactionHelper runs it inside its own save).
const saveWithRows = async (save, addRows) => {
  // The helper always runs extraWrites inside its save. If it did not here,
  // this call was merged into a save already running (double click) — that
  // save added the rows, so nothing is added again.
  return save({ extraWrites: (w, ctx) => addRows(w, ctx) });
};

// INV-11 / INV-15: the transfer's stock rows, added to `writer`. Its old rows (if
// any) are replaced in the same commit, and the items' running averages move with
// them (old rows out, new rows in) — so a retry never moves stock twice.
const replaceStockRows = async (companyId, writer, { transactionId, stock, removeOld, financialYearId, userId }) => {
  const [oldRows, newRows] = await Promise.all([
    removeOld ? listStockFlowRows(companyId, transactionId) : Promise.resolve([]),
    stock ? generateStockFlow(companyId, stock, userId, { writer }) : Promise.resolve([]),
  ]);
  const kept = new Set(newRows.map(r => r.id));
  oldRows.forEach(r => { if (!kept.has(r.id)) writer.delete(doc(db, 'companies', companyId, 'stockFlow', r.id)); });
  if (oldRows.length) writer.afterCommit(() => { applyEntriesToStockCache(companyId, oldRows, -1); invalidateItemStock(companyId); });
  try {
    await costRowsChange(companyId, writer, { added: newRows, removed: oldRows, financialYearId });
  } catch (e) {
    console.warn('[stock] item average not updated (Data Repair will rebuild it):', e && e.message);
  }
  return newRows;
};

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', unitCost: 0, lineTotal: 0, trackingType: 'None', batchNumber: '', serialNumbers: [] };
const STATUS_COLORS = { Draft: colors.warning.main, InTransit: colors.info.main, Completed: colors.success.main, Cancelled: colors.error.main };

const BranchTransfer = () => {
  const { activeCompany, currentUser, companyUser, activeFinancialYear, activeBranch, branches, isBranchModuleEnabled } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'stockTransfer');
  const isAdmin = companyUser?.role === 'admin';
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    fromBranchId: '', fromBranchName: '',
    toBranchId: '', toBranchName: '',
    fromLocationId: '', fromLocationName: '',
    toLocationId: '', toLocationName: '',
    reason: '', notes: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});
  const [availableSerials, setAvailableSerials] = useState({});
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stockBalances, setStockBalances] = useState({});
  const [printConfig, setPrintConfig] = useState(null);

  // Available branches for the user
  const userBranches = useMemo(() => {
    if (!branches || branches.length === 0) return [];
    if (isAdmin) return branches;
    return branches.filter(b => (companyUser?.assignedBranches || []).includes(b.id));
  }, [branches, isAdmin, companyUser]);

  const loadRecords = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      // Load all ST transactions that have fromBranchId or toBranchId (branch transfers)
      const data = await getTransactions(activeCompany.id, { tranTypeCode: TRAN_TYPE_CODE });
      // Filter to only branch transfers (have fromBranchId and toBranchId)
      const branchTransfers = data.filter(r => r.fromBranchId && r.toBranchId);
      // Further filter by active branch — show transfers where user's branch is source or destination
      const filtered = activeBranch && activeBranch.id !== 'all'
        ? branchTransfers.filter(r => r.fromBranchId === activeBranch.id || r.toBranchId === activeBranch.id)
        : branchTransfers;
      setRecords(filtered);
    } catch (error) { console.error('Error:', error); } finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true).sort((a, b) => (a.locationName || '').localeCompare(b.locationName || '')));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  useEffect(() => {
    loadRecords(); loadItems(); loadLocations(); loadEmployees(); loadJobs();
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [loadRecords, loadItems, loadLocations, loadEmployees, loadJobs, activeCompany]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) { const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) ||
        r.fromBranchName?.toLowerCase().includes(q) ||
        r.toBranchName?.toLowerCase().includes(q) ||
        r.fromLocationName?.toLowerCase().includes(q) ||
        r.toLocationName?.toLowerCase().includes(q));
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => ({ ...line, unitPrice: line.unitCost || 0, lineTotal: Math.round(line.quantity * line.unitCost * 100) / 100 });
  const totals = useMemo(() => ({ totalValue: Math.round(lineItems.map(calculateLine).reduce((s, l) => s + l.lineTotal, 0) * 100) / 100 }), [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleFromBranchChange = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    // Reset fromLocationId because the previously-chosen location may belong to a different branch.
    setFormData(prev => ({ ...prev, fromBranchId: branchId, fromBranchName: branch?.branchName || '', fromLocationId: '', fromLocationName: '' }));
  };

  const handleToBranchChange = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    setFormData(prev => ({ ...prev, toBranchId: branchId, toBranchName: branch?.branchName || '', toLocationId: '', toLocationName: '' }));
  };

  const handleFromLocationChange = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    setFormData(prev => ({ ...prev, fromLocationId: locationId, fromLocationName: loc?.locationName || '' }));
  };

  // Whenever the source location or branch changes (or after edit-load), reload
  // available batches/serials for every sourced line so the popovers reflect
  // stock physically present at SOURCE branch+location only.
  useEffect(() => {
    const locId = formData.fromLocationId;
    const brId = formData.fromBranchId || null;
    if (!locId || !activeCompany?.id) return;
    const itemIdsToLoad = new Set();
    const batchItemIds = new Set();
    const serialItemIds = new Set();
    lineItems.forEach(l => {
      if (!l.itemId) return;
      if (l.trackingType === 'Batch') { batchItemIds.add(l.itemId); itemIdsToLoad.add(l.itemId); }
      if (l.trackingType === 'Serial') { serialItemIds.add(l.itemId); itemIdsToLoad.add(l.itemId); }
    });
    if (itemIdsToLoad.size === 0) return;
    batchItemIds.forEach(iid => {
      getAvailableBatches(activeCompany.id, iid, locId, brId).then(b => setAvailableBatches(prev => ({ ...prev, [iid]: b })));
    });
    serialItemIds.forEach(iid => {
      getAvailableSerials(activeCompany.id, iid, locId, brId).then(s => setAvailableSerials(prev => ({ ...prev, [iid]: s })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fromLocationId, formData.fromBranchId, activeCompany?.id, lineItems.length]);

  const handleToLocationChange = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    setFormData(prev => ({ ...prev, toLocationId: locationId, toLocationName: loc?.locationName || '' }));
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev]; updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure, unitCost: patch.unitCost };
        }
      }
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure, unitCost: seeded.unitCost };
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          updated[index].taxRate = item.gstRate || 0;
          updated[index].taxable = (item.gstRate || 0) > 0;
          // When fromLocation is already set, eagerly load for this item.
          // Otherwise the useEffect above will pick it up once a location is chosen.
          // When fromLocation is already set, eagerly load for this item.
          // Otherwise the useEffect above will pick it up once a location is chosen.
          if (item.trackingType === 'Batch' && formData.fromLocationId) {
            getAvailableBatches(activeCompany.id, value, formData.fromLocationId, formData.fromBranchId || null).then(batches => {
              setAvailableBatches(prev => ({ ...prev, [value]: batches }));
              if (batches.length > 0) {
                setLineItems(prev => {
                  const u = [...prev];
                  if (u[index] && u[index].itemId === value) u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial' && formData.fromLocationId) {
            getAvailableSerials(activeCompany.id, value, formData.fromLocationId, formData.fromBranchId || null).then(serials => {
              setAvailableSerials(prev => ({ ...prev, [value]: serials }));
            });
          }
        }
      }
      updated[index] = calculateLine(updated[index]); return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      fromBranchId: activeBranch?.id !== 'all' ? activeBranch?.id || '' : '',
      fromBranchName: activeBranch?.id !== 'all' ? activeBranch?.branchName || '' : '',
      toBranchId: '', toBranchName: '',
      fromLocationId: '', fromLocationName: '',
      toLocationId: '', toLocationName: '',
      reason: '', notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]); setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };

  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({
      docNumber: rec.docNumber || '',
      docDate: rec.docDate || new Date().toISOString().split('T')[0],
      fromBranchId: rec.fromBranchId || rec.branchId || '',
      fromBranchName: rec.fromBranchName || rec.branchName || '',
      toBranchId: rec.toBranchId || '',
      toBranchName: rec.toBranchName || '',
      fromLocationId: rec.fromLocationId || '', fromLocationName: rec.fromLocationName || rec.fromLocation || '',
      toLocationId: rec.toLocationId || '', toLocationName: rec.toLocationName || rec.toLocation || '',
      reason: rec.reason || '', notes: rec.notes || '', status: rec.status || 'Draft',
      employeeId: rec.employeeId || '', employeeName: rec.employeeName || '',
      jobId: rec.jobId || '', jobName: rec.jobName || ''
    });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };

  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  useOpenFromReport(records, handleView);

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.fromBranchId) { alert('Please select From Branch'); return; }
    if (!formData.toBranchId) { alert('Please select To Branch'); return; }
    if (!formData.fromLocationId || !formData.toLocationId) { alert('Please select both From and To locations'); return; }
    if (formData.fromBranchId === formData.toBranchId && formData.fromLocationId === formData.toLocationId) {
      alert('Source and destination cannot be the same branch and location'); return;
    }

    const validLines = lineItems.filter(l => l.itemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one item'); return; }

    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch selection is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        if (serials.length !== (line.quantity || 0)) {
          alert(`"${line.itemName || line.description}" requires exactly ${line.quantity} serial number(s). You selected ${serials.length}.`); return;
        }
      }
    }

    if (!activeFinancialYear?.id) { alert('No active financial year'); return; }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        return { ...calculateLine(l), lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
      });
      // INV-11: a completed transfer stays Completed when edited — its stock is
      // re-posted from the edited lines in the same save, never silently reversed.
      const status = (editingRecord && editingRecord.status === 'Completed') ? 'Completed' : (saveStatus || 'Draft');
      const data = {
        docDate: formData.docDate,
        fromBranchId: formData.fromBranchId,
        fromBranchName: formData.fromBranchName,
        toBranchId: formData.toBranchId,
        toBranchName: formData.toBranchName,
        fromLocationId: formData.fromLocationId,
        fromLocationName: formData.fromLocationName,
        toLocationId: formData.toLocationId,
        toLocationName: formData.toLocationName,
        fromLocation: formData.fromLocationName,
        toLocation: formData.toLocationName,
        locationId: formData.fromLocationId,
        locationName: formData.fromLocationName,
        // branchId = source branch for backward compatibility
        branchId: formData.fromBranchId,
        branchName: formData.fromBranchName,
        isBranchTransfer: true, // Flag to distinguish from regular stock transfers
        reason: formData.reason,
        notes: formData.notes,
        status,
        totalValue: totals.totalValue,
        lines: calculatedLines,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        financialYearId: activeFinancialYear.id
      };

      // Stock flow on Completed — OUT at source branch, IN at destination branch.
      // Re-saving an already-Completed transfer REPLACES its rows in the SAME save
      // as the document (INV-11 / INV-15), so the IN/OUT entries always match the
      // latest line set. Lines are unit-enriched exactly as the saved document's
      // lines (what Complete posts from); rows stay in the transfer's own year.
      let addRows = null;
      if (status === 'Completed') {
        const fyId = editingRecord?.financialYearId || activeFinancialYear.id;
        const enrichedLines = await enrichTransactionLines(activeCompany.id, calculatedLines);
        const stockLines = enrichedLines.map(l => ({
          ...l,
          direction: 'out',
          branchId: formData.fromBranchId,
          branchName: formData.fromBranchName,
          toBranchId: formData.toBranchId,
          toBranchName: formData.toBranchName,
          fromLocationId: formData.fromLocationId,
          fromLocationName: formData.fromLocationName,
          toLocationId: formData.toLocationId,
          toLocationName: formData.toLocationName,
          batchNumber: l.batchNumber || '',
          serialNumbers: l.serialNumbers || []
        }));
        addRows = (w, ctx) => replaceStockRows(activeCompany.id, w, {
          transactionId: ctx.transactionId, removeOld: !!editingRecord, financialYearId: fyId, userId: currentUser.uid,
          stock: {
            transactionId: ctx.transactionId,
            tranTypeCode: TRAN_TYPE_CODE,
            docNumber: ctx.docNumber,
            docDate: formData.docDate,
            financialYearId: fyId,
            branchId: formData.fromBranchId,
            branchName: formData.fromBranchName,
            toBranchId: formData.toBranchId,
            toBranchName: formData.toBranchName,
            status: 'Completed',
            locationId: formData.fromLocationId,
            locationName: formData.fromLocationName,
            lines: stockLines
          }
        });
      }

      let savedDocId;
      let newTranData = null;
      if (editingRecord) {
        if (addRows) await saveWithRows((opts) => updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid, opts), addRows);
        else await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        savedDocId = editingRecord.id;
      } else {
        const tran = { ...data, tranTypeCode: TRAN_TYPE_CODE };
        newTranData = addRows
          ? await saveWithRows((opts) => createTransaction(activeCompany.id, tran, currentUser.uid, opts), addRows)
          : await createTransaction(activeCompany.id, tran, currentUser.uid);
        savedDocId = newTranData.id;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, status }
        : { ...newTranData, status };
      setRecords(prev => editingRecord
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) { console.error('Error:', error); alert('Error saving: ' + error.message); } finally { setSaving(false); }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try { await deleteTransaction(activeCompany.id, rec.id); await loadRecords(); }
    catch (error) { console.error('Error:', error); }
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to cancel.'); return; }
    if (rec.status === 'Cancelled') return;
    if (rec.status === 'Completed' && !window.confirm('This will reverse the stock movement. Continue?')) return;
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      if (rec.status === 'Completed') await deleteStockFlow(activeCompany.id, rec.id);
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
    } catch (error) { console.error('Error:', error); }
  };

  const handleComplete = async (rec) => {
    if (!canEdit) { alert('You do not have permission to complete.'); return; }
    if (rec.status !== 'InTransit') return;
    if (!window.confirm(`Mark ${rec.docNumber} as Completed?`)) return;
    try {
      // INV-15: status + stock rows + item averages in ONE save (the status used
      // to be saved first and the stock separately — a dropped connection left a
      // Completed transfer with no stock moved). Safe to retry: the rows are
      // replaced, never added twice.
      const completionLines = (rec.lines || []).map(l => ({
        ...l,
        direction: 'out',
        branchId: rec.fromBranchId || rec.branchId || null,
        branchName: rec.fromBranchName || rec.branchName || null,
        toBranchId: rec.toBranchId || null,
        toBranchName: rec.toBranchName || null,
        fromLocationId: rec.fromLocationId || null,
        fromLocationName: rec.fromLocationName || rec.fromLocation || '',
        toLocationId: rec.toLocationId || null,
        toLocationName: rec.toLocationName || rec.toLocation || '',
        batchNumber: l.batchNumber || '',
        serialNumbers: l.serialNumbers || []
      }));
      await saveWithRows(
        (opts) => updateTransaction(activeCompany.id, rec.id, { status: 'Completed', completedAt: new Date().toISOString() }, currentUser.uid, opts),
        (w) => replaceStockRows(activeCompany.id, w, {
          transactionId: rec.id, removeOld: true, financialYearId: activeFinancialYear.id, userId: currentUser.uid,
          stock: {
            transactionId: rec.id, tranTypeCode: TRAN_TYPE_CODE,
            docNumber: rec.docNumber, docDate: rec.docDate,
            financialYearId: activeFinancialYear.id,
            branchId: rec.fromBranchId || rec.branchId || null,
            branchName: rec.fromBranchName || rec.branchName || null,
            toBranchId: rec.toBranchId || null,
            toBranchName: rec.toBranchName || null,
            status: 'Completed',
            locationId: rec.fromLocationId || null,
            locationName: rec.fromLocationName || rec.fromLocation,
            lines: completionLines
          }
        })
      );
      await loadRecords();
    } catch (error) { console.error('Error:', error); alert('Error completing: ' + error.message); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('branchTransfer', {
      ...data,
      fromBranchName: data.fromBranchName || data.branchName || '',
      toBranchName: data.toBranchName || '',
      fromLocationName: data.fromLocationName || data.fromLocation || '',
      toLocationName: data.toLocationName || data.toLocation || ''
    }, activeCompany, printConfig, activeBranch);
  };

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar><SearchBox><FiSearch /><input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></SearchBox>
        <FilterGroup><StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="All">All Status</option><option value="Draft">Draft</option><option value="InTransit">In Transit</option><option value="Completed">Completed</option><option value="Cancelled">Cancelled</option>
        </StatusFilterSelect>{canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Branch Transfer</TranAddButton>}</FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Branch Transfers</h3><p>{records.length === 0 ? 'Create your first branch transfer' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Branch Transfer</TranAddButton>}</TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>
          <SortableTh field="fromBranchName">From Branch</SortableTh><SortableTh field="fromLocationName">From Location</SortableTh>
          <SortableTh field="toBranchName">To Branch</SortableTh><SortableTh field="toLocationName">To Location</SortableTh>
          <SortableTh field="totalValue" style={{ textAlign: 'right' }}>Value</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td>
              <Td>{rec.docDate}</Td>
              <Td>{rec.fromBranchName || rec.branchName || '-'}</Td>
              <Td>{rec.fromLocationName || rec.fromLocation || '-'}</Td>
              <Td>{rec.toBranchName || '-'}</Td>
              <Td>{rec.toLocationName || rec.toLocation || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && rec.status !== 'Cancelled' && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canEdit && rec.status === 'InTransit' && <IconBtn title="Complete" onClick={() => handleComplete(rec)}><FiCheck /></IconBtn>}
                {canDelete && rec.status !== 'Cancelled' && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && rec.status !== 'Cancelled' && <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}><FiXCircle /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}</tbody></Table></SortContext.Provider></TableContainer>
      )}
      <SummaryBar><span>Total: <strong>{filteredRecords.length}</strong></span>
        <span>Draft: <strong>{records.filter(r => r.status === 'Draft').length}</strong> | In Transit: <strong>{records.filter(r => r.status === 'InTransit').length}</strong> | Completed: <strong>{records.filter(r => r.status === 'Completed').length}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null; const rec = viewingRecord;
    return (<>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
        <FormActions>
          {canChange && rec.status !== 'Cancelled' && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
          {canEdit && rec.status === 'InTransit' && <SaveButton $approve onClick={() => handleComplete(rec)}><FiCheck /> Complete</SaveButton>}
          <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Transfer Details</SectionTitle><InfoGrid>
        <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
        <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
        <InfoItem><label>From Branch</label><span>{rec.fromBranchName || rec.branchName || '-'}</span></InfoItem>
        <InfoItem><label>From Location</label><span>{rec.fromLocationName || rec.fromLocation || '-'}</span></InfoItem>
        <InfoItem><label>To Branch</label><span>{rec.toBranchName || '-'}</span></InfoItem>
        <InfoItem><label>To Location</label><span>{rec.toLocationName || rec.toLocation || '-'}</span></InfoItem>
        <InfoItem><label>Reason</label><span>{rec.reason || '-'}</span></InfoItem>
        <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
        <InfoItem><label>Job</label><span>{rec.jobName || '-'}</span></InfoItem>
      </InfoGrid></FormCard>
      <FormCard><SectionTitle>Items</SectionTitle><TableContainer><Table><thead><tr><Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Qty</Th><Th style={{ textAlign: 'right' }}>Unit Cost</Th><Th style={{ textAlign: 'right' }}>Total</Th></tr></thead><tbody>
        {(rec.lines || []).map((line, i) => (<Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td><Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td><Td style={{ textAlign: 'right' }}>{formatAmount(line.unitCost)}</Td><Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td></Tr>))}
      </tbody></Table></TableContainer>
        <TotalsSection><TotalsTable><tbody><TotalRow><td>Total Value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalValue)}</td></TotalRow></tbody></TotalsTable></TotalsSection>
      </FormCard>
      {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
    </>);
  };

  const renderFormView = () => (
    <>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Branch Transfer'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && (editingRecord?.status === 'Completed'
            ? <SaveButton $approve onClick={() => handleSave('Completed')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Update & Re-post'}</SaveButton>
            : <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>)}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && <SaveButton $approve onClick={() => handleSave('InTransit')} disabled={saving}><FiTruck /> Dispatch</SaveButton>}
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Transfer Details</SectionTitle><FormGrid>
        <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
        <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
        <FormGroup>
          <Label>From Branch *</Label>
          <Select value={formData.fromBranchId} onChange={e => handleFromBranchChange(e.target.value)}>
            <option value="">-- Select Source Branch --</option>
            {userBranches.map(b => (
              <option key={b.id} value={b.id}>{b.branchCode ? b.branchCode + ' - ' : ''}{b.branchName}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>From Location *</Label>
          <Select value={formData.fromLocationId} onChange={e => handleFromLocationChange(e.target.value)}>
            <option value="">-- Select Source Location --</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.locationCode ? loc.locationCode + ' - ' : ''}{loc.locationName}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>To Branch *</Label>
          <Select value={formData.toBranchId} onChange={e => handleToBranchChange(e.target.value)}>
            <option value="">-- Select Destination Branch --</option>
            {(branches || []).map(b => (
              <option key={b.id} value={b.id}>{b.branchCode ? b.branchCode + ' - ' : ''}{b.branchName}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>To Location *</Label>
          <Select value={formData.toLocationId} onChange={e => handleToLocationChange(e.target.value)}>
            <option value="">-- Select Destination Location --</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.locationCode ? loc.locationCode + ' - ' : ''}{loc.locationName}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup $span2><Label>Reason</Label><Input value={formData.reason} onChange={e => handleHeaderChange('reason', e.target.value)} placeholder="Reason for transfer" /></FormGroup>
        <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
        <FormGroup>
          <Label>Employee</Label>
          <Select value={formData.employeeId} onChange={e => {
            const emp = employees.find(em => em.id === e.target.value);
            handleHeaderChange('employeeId', e.target.value);
            handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
          }}>
            <option value="">-- Select Employee --</option>
            {employees.map(emp => (<option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Job</Label>
          <Select value={formData.jobId} onChange={e => {
            const job = jobs.find(j => j.id === e.target.value);
            handleHeaderChange('jobId', e.target.value);
            handleHeaderChange('jobName', job?.jobName || '');
          }}>
            <option value="">-- Select Job --</option>
            {jobs.map(job => (<option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>))}
          </Select>
        </FormGroup>
      </FormGrid></FormCard>
      <FormCard>
        <LineItemsHeader><SectionTitle style={{ margin: 0 }}>Items</SectionTitle><AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton></LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh><LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh><LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh><LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Cost</LineTh><LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
          {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Batch</LineTh>}
          {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => { const calc = calculateLine(line); return (
            <tr key={index}>
              <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
              <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
              <LineTd><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /><LineUnitPicker item={items.find(i => i.id === line.itemId)} line={line} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} /></div></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.unitCost} onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
              {lineItems.some(l => l.trackingType === 'Batch') && (
                <LineTd>{line.trackingType === 'Batch' ? (
                  <select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120, border: '1px solid #ddd', borderRadius: '4px' }}>
                    <option value="">Select Batch</option>
                    {(availableBatches[line.itemId] || []).map(b => (<option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining})</option>))}
                  </select>
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
              )}
              {lineItems.some(l => l.trackingType === 'Serial') && (
                <LineTd>{line.trackingType === 'Serial' ? (
                  <SerialSelectPopover availableSerials={availableSerials[line.itemId] || []} selectedSerials={line.serialNumbers || []}
                    requiredCount={line.quantity || 0} onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)} />
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
              )}
              <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
            </tr>); })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody><TotalRow><td>Total Value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalValue)}</td></TotalRow></tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard><SectionTitle>Notes</SectionTitle><FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid></FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('InTransit'); },
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Branch Transfer" breadcrumb="Store / Branch Transfer">
      {viewMode === 'list' && renderListView()}{viewMode === 'form' && renderFormView()}{viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default BranchTransfer;
